import _sortBy from 'lodash/sortBy';

import Utils from 'services/utils';

const UI_READINESS_TIMEOUT_DELAY = 20;

const TOAST_CONFIG = {
    hideDelay: 1500
};

export default class EditNavBarPopupController {
    /** @ngInject */
    constructor(
        DialogService,
        onDone,
        treeConfig,
        EventEmitter,
        NavigationService,
        Loader,
        ViewsApi,
        CategoriesApi,
        ToastService,
        $q,
        $scope
    ) {
        this.NavigationService = NavigationService;
        this.DialogService = DialogService;
        this.Loader = Loader;
        this.eventEmitter = EventEmitter;
        this.onDone = onDone;
        this.ViewsApi = ViewsApi;
        this.CategoriesApi = CategoriesApi;
        this.ToastService = ToastService;
        this.$q = $q;
        this.$scope = $scope;

        treeConfig.dragClass = 'angular-ui-tree-drag ui-tree--views ui-tree';

        this.isLoading = true;

        this.treeOptions = {
            beforeDrop: EditNavBarPopupController.beforeNodeDrop,
            accept: ({ $modelValue: draggedItem }, { $nodeScope: dropScope }) => {
                if (!dropScope) {
                    return true;
                }

                return draggedItem.type !== 'CATEGORY';
            },
            dropped: this.onItemDropped.bind(this)
        };

        this.addCategoryVisible = false;
        this.savingItemPromise = null;

        this.loadCategories();
    }

    onItemDropped(ev) {
        const item = ev.source.nodeScope.$modelValue;
        if (item.type === 'VIEW') {
            const newParent = ev.dest.nodesScope.$nodeScope
                ? ev.dest.nodesScope.$nodeScope.$modelValue
                : null;

            this.ViewsApi.saveView(item.id, {
                name: item.title,
                search_query: item.search_query,
                category_id: newParent ? newParent.id : '',
                position: ev.dest.index + 1
            }).then(() => {
                this.ToastService.showSimpleTranslated('navbar_view_was_moved', TOAST_CONFIG);
            }).catch(() => {
                this.ToastService.showSimpleTranslated('navbar_view_wasnt_moved', TOAST_CONFIG);
            });
        }

        if (item.type === 'CATEGORY') {
            this.CategoriesApi.saveCategory(item.id, {
                parent_id: item.parent_id,
                name: item.title,
                position: ev.dest.index + 1
            }).then(() => {
                this.ToastService.showSimpleTranslated('navbar_category_was_moved', TOAST_CONFIG);
            }).catch(() => {
                this.ToastService.showSimpleTranslated('navbar_category_wasnt_moved', TOAST_CONFIG);
            });
        }
    }

    static beforeNodeDrop(event) {
        const source = event.source.nodeScope.$modelValue;
        const destination = event.dest.nodesScope.$nodeScope
            ? event.dest.nodesScope.$nodeScope.$modelValue
            : undefined;

        return destination ? source.type !== destination.type : true;
    }

    expandAll() {
        this.$scope.$broadcast('angular-ui-tree:expand-all');
    }

    collapseAll() {
        this.$scope.$broadcast('angular-ui-tree:collapse-all');
    }

    showAddCategoryForm() {
        this.addCategoryVisible = true;
    }

    hideAddCategoryForm() {
        this.addCategoryVisible = false;
        this.resetAddCategoryForm();
    }

    resetAddCategoryForm() {
        this.categoryName = '';
        this.addCategoryForm.$setPristine();
    }

    deleteItem(item) {
        let promise;
        let itemType;

        if (item.type === 'CATEGORY') {
            itemType = 'category';
            promise = this.CategoriesApi.deleteCategory(item.id);
        } else {
            itemType = 'view';
            promise = this.ViewsApi.deleteView(item.id);
        }

        promise.then(() => {
            this.loadCategories();
            this.ToastService.showSimpleTranslated(`navbar_${itemType}_was_removed`, TOAST_CONFIG);
        }).catch(() => {
            this.ToastService.showSimpleTranslated(`navbar_${itemType}_wasnt_removed`, TOAST_CONFIG);
        });
    }

    editItem(item, inputId) {
        if (this.editingItemInputId === inputId) {
            return;
        }

        this.$q.when(this.savingItemPromise).then(() => {
            this.editingItem = item;
            this.originalTitle = this.editingItem.title;
            this.editingItemInputId = inputId;

            const input = document.getElementById(inputId);
            setTimeout(() => { input.select(); input.focus(); }, UI_READINESS_TIMEOUT_DELAY);
        });
    }

    saveItem() {
        if (!this.editingItem || this.editingItem.title === this.originalTitle) {
            return;
        }

        this.showProgress();
        const item = this.editingItem;

        let promise;
        let itemType;

        if (item.type === 'CATEGORY') {
            itemType = 'category';
            promise = this.CategoriesApi.saveCategory(item.id, {
                name: item.title,
                position: item.position
            });
        } else {
            itemType = 'view';
            promise = this.ViewsApi.saveView(item.id, {
                name: item.title,
                search_query: item.search_query,
                category_id: item.category_id,
                position: item.position
            });
        }

        this.savingItemPromise = promise
            .then(() => {
                this.ToastService.showSimpleTranslated(`navbar_${itemType}_was_updated`, TOAST_CONFIG);
            })
            .catch(() => {
                item.title = this.originalTitle;
                this.ToastService.showSimpleTranslated(`navbar_${itemType}_wasnt_updated`, TOAST_CONFIG);
            }).finally(() => {
                this.hideProgress();
                this.originalTitle = null;
                this.editingItem = null;
                this.editingItemInputId = null;
            });
    }

    addCategory() {
        this.CategoriesApi.createCategory({
            name: this.categoryName
        }).then(() => {
            this.loadCategories();
            this.hideAddCategoryForm();
            this.ToastService.showSimpleTranslated('navbar_category_was_created', TOAST_CONFIG);
        }).catch(() => {
            this.ToastService.showSimpleTranslated('navbar_category_wasnt_created', TOAST_CONFIG);
        });
    }

    showProgress() {
        this.isLoading = true;
        this.Loader.show();
    }

    hideProgress() {
        this.isLoading = false;
        this.Loader.hide();
    }

    loadCategories() {
        this.showProgress();

        this.NavigationService.getSensorsAndConnectors().then((data) => {
            this.categories = _sortBy(Utils.parseNavigationData({
                data,
                removeKeys: ['name', 'order', 'created', 'updated'],
                addEmptyItemsArrayOnGroup: true
            }), 'position');

            this.categories.forEach((item) => {
                if ('items' in item) {
                    item.items = _sortBy(item.items, 'position');
                }
            });

            this.hideProgress();

            this.addNoDropPropertyToView();
        },
        this.hideProgress.bind(this));
    }

    addNoDropPropertyToView() {
        const addNoDrop = (item) => {
            if (item.type === 'VIEW') {
                item.nodrop = true;
            }

            if (item.items && item.items.length) {
                item.items.forEach(addNoDrop);
            }
        };

        this.categories.forEach(addNoDrop);
    }

    cancel() {
        this.hideProgress();
        this.DialogService.cancel();
    }

    done() {
        this.showProgress();
        this.onDone();
        this.cancel();
    }

    blurFocusedItems({ target }) {
        if (this.editingItem && this.editingItemInputId && !target.closest('.entity-label')) {
            this.editingItem = null;
            this.editingItemInputId = null;
        }
    }
}



// WEBPACK FOOTER //
// ./app/common/edit-navbar/popup/popup.controller.js